import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";
import getCardSrc from "utilities/creditCards/getCardSrc";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import BadgeIcon from "@mui/icons-material/Badge";
import CakeIcon from "@mui/icons-material/Cake";
import Accordion from "components/Аccordion";

function PersonalCredentials({ client }) {
  const { t } = useTranslation();

  const renderPhones = () => {
    if (!client.phones || client.phones.length === 0) {
      return <div className="accordion-empty-state">{t("user.no_phones_monitored")}</div>;
    }

    return (
      <div className="accordion-grid-item">
        <div className="accordion-grid-item-title">{t("user.phones_monitored")}</div>
        <div className="accordion-grid-item-content">
          {client.phones.map((phone, index) => (
            <div key={`phone-${index}`} className="item-entry">
              <PhoneIcon style={{ fontSize: 16, marginRight: 8, color: "var(--blue-color)" }} />
              <span>
                {phone.prefix || ""} {phone.number || ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAddresses = () => {
    if (!client.addresses || client.addresses.length === 0) {
      return <div className="accordion-empty-state">{t("user.no_addresses_monitored")}</div>;
    }

    return (
      <div className="accordion-grid-item">
        <div className="accordion-grid-item-title">{t("user.addresses_monitored")}</div>
        <div className="accordion-grid-item-content">
          {client.addresses.map((address, index) => (
            <div key={`address-${index}`} className="accordion-list-item">
              {address}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderIbans = () => {
    if (!client.ibans || client.ibans.length === 0) {
      return <div className="accordion-empty-state">{t("user.no_ibans_monitored")}</div>;
    }

    return (
      <div className="accordion-grid-item">
        <div className="accordion-grid-item-title">{t("user.ibans_monitored")}</div>
        <div className="accordion-grid-item-content">
          {client.ibans.map((iban, index) => (
            <div key={`iban-${index}`} className="accordion-list-item">
              <AccountBalanceIcon
                style={{ fontSize: 16, marginRight: 8, color: "var(--blue-color)" }}
              />
              {iban}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderUserCreditCards = () => {
    if (!client.userCreditCards || client.userCreditCards.length === 0) {
      return (
        <div className="accordion-empty-state">{t("user.no_personal_credit_cards_monitored")}</div>
      );
    }

    return (
      <div className="accordion-grid-item">
        <div className="accordion-grid-item-title">{t("user.personal_credit_cards_monitored")}</div>
        <div className="accordion-grid-item-content">
          {client.userCreditCards.map((userCC, index) => (
            <div key={`user-credit-card-${index}`} className="item-entry">
              <img src={getCardSrc(userCC.type)} className="accordion-card-icon" alt="card type" />
              <span>***{userCC.blurryNumber}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderIdentifiers = () => {
    if (!client.identifiers || client.identifiers.length === 0) {
      return <div className="accordion-empty-state">{t("user.no_identifiers_monitored")}</div>;
    }

    return (
      <div className="accordion-grid-item">
        <div className="accordion-grid-item-title">{t("user.identifiers_monitored")}</div>
        <div className="accordion-grid-item-content">
          {client.identifiers.map((identifier, index) => (
            <div key={`identifier-${index}`} className="accordion-list-item">
              <BadgeIcon style={{ fontSize: 16, marginRight: 8, color: "var(--blue-color)" }} />
              {identifier.type}: {identifier.value}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBirthDates = () => {
    if (!client.birthDates || client.birthDates.length === 0) {
      return <div className="accordion-empty-state">{t("user.no_birth_dates_monitored")}</div>;
    }

    return (
      <div className="accordion-grid-item">
        <div className="accordion-grid-item-title">{t("user.birth_dates_monitored")}</div>
        <div className="accordion-grid-item-content">
          {client.birthDates.map((birthDate, index) => (
            <div key={`birth-date-${index}`} className="item-entry">
              <CakeIcon style={{ fontSize: 16, marginRight: 8, color: "var(--blue-color)" }} />
              <span>{birthDate ? moment(birthDate).format("DD/MM/YYYY") : ""}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="personal-credentials-container">
      <Accordion title={t("user.phones_monitored")} icon={<PhoneIcon />} defaultExpanded={false}>
        {renderPhones()}
      </Accordion>

      <Accordion title={t("user.addresses_monitored")} icon={<HomeIcon />} defaultExpanded={false}>
        {renderAddresses()}
      </Accordion>

      <Accordion
        title={t("user.ibans_monitored")}
        icon={<AccountBalanceIcon />}
        defaultExpanded={false}
      >
        {renderIbans()}
      </Accordion>

      <Accordion
        title={t("user.personal_credit_cards_monitored")}
        icon={<CreditCardIcon />}
        defaultExpanded={false}
      >
        {renderUserCreditCards()}
      </Accordion>

      <Accordion
        title={t("user.identifiers_monitored")}
        icon={<BadgeIcon />}
        defaultExpanded={false}
      >
        {renderIdentifiers()}
      </Accordion>

      <Accordion
        title={t("user.birth_dates_monitored")}
        icon={<CakeIcon />}
        defaultExpanded={false}
      >
        {renderBirthDates()}
      </Accordion>
    </div>
  );
}

PersonalCredentials.propTypes = {
  client: PropTypes.object.isRequired,
};

export default PersonalCredentials;
